/** View models and formatting of the multi-plan roll-up (values come from the API). */
import type { WaveWeek } from './model'
import type { WavePlan } from './routes'

/** A fiscal-year group of plans. */
export interface RollupCalendar {
  id: string
  label: string
  year: number
}

export interface RollupMetrics {
  percent: number | null
  units: number
  skuCount: number | null
  repackSkuCount: number | null
  cube: number | null
}

export type RollupMetric = keyof RollupMetrics
export type RollupChartMetric = 'percent' | 'units' | 'cube'
export interface RollupHighlight {
  planId: string
  week?: number
}

/** The plan details sheet. ``null`` means the value is not available. */
export interface RollupPlanDetails {
  hierarchyCode: string | null
  description: string
  pogDescription: string | null
  location: string | null
  stores: number | null
  sellableCount: number | null
  shippingCount: number | null
  piabCount: number | null
  cicoCount: number | null
  repackCount: number | null
  repackShipQty: number | null
}

/**
 * One plan of a group. ``waves`` and ``activeWeeks`` use the group's numeric
 * week ids (``WaveWeek.week``), which are unique across fiscal years.
 */
export interface RollupPlan {
  plan: WavePlan
  details: RollupPlanDetails
  setDate: string
  baseUnits: number
  waves: Record<number, RollupMetrics>
  season: RollupMetrics
  activeWeeks: number[]
  waveCount: number
  needsReview: boolean
}

export interface RollupGroup {
  calendar: RollupCalendar
  plans: RollupPlan[]
  weeks: WaveWeek[]
  activeWeeks: WaveWeek[]
  waves: Record<number, RollupMetrics>
  baseUnits: number
  season: RollupMetrics
}

const numbers = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })
const percentages = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

export function formatRollupValue(value: number | null, metric: RollupMetric) {
  if (value === null || !Number.isFinite(value)) return '—'
  return metric === 'percent'
    ? percentages.format(value)
    : numbers.format(value)
}

export function formatRollupDate(date: string) {
  if (!date) return '—'
  const [year, month, day] = date.split('-')
  return `${month}/${day}/${year.slice(-2)}`
}
